import * as React from "react";

import { DisplayData } from "mui-datatables";

export type Item = {
  tableIndex: number;
  dataIndex: number;
};

export type PageRows = Map<string, Item>;

export interface RowStore {
  add: (resourceId: string, item: Item) => void;
  get: () => PageRows;
  del: (resourceId: string) => void;
  lookup: (resourceID: string) => Item;
  checkAllForPage: () => void;
  checkAllForTable: () => void;
  clear: () => void;
  clearPage: () => void;
  allCheckedPage: () => boolean;
  partiallyCheckedPage: () => boolean;
  isChecked: (resourceId: string) => boolean;
  syncTable: (displayData: DisplayData, index: number) => void;
}

// useRowStore hook keeps track of selected rows based on resource ID
export const useRowStore = (data: { id: string }[], rowsPerPage: number, pageNumber: number) => {
  let table: DisplayData = [];
  let resourceIdIndex = 0;

  const [selectedRows, setSelectedRows] = React.useState<PageRows>(new Map()); // Needs map data structure, with key being resource ID and value being row index data

  // On data change, we need to make sure that any selections that are no longer in the new data set are removed from selection state
  React.useEffect(() => {
    if (data) {
      const ids = data?.map((d) => {
        return d?.id;
      });

      setSelectedRows((selected) => {
        const newMap = new Map();
        for (const id of ids) {
          const found = selected.get(id);
          if (found) {
            newMap.set(String(id), found);
          }
        }
        return newMap;
      });
    }
  }, [data]);

  // Function that perform actions only on rows that are visible on the selected page
  const doPerPage = React.useCallback(
    (cb, table) => {
      if (table?.length < rowsPerPage) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        pageNumber = 0; // Workaround because mui datatables doesn't trigger a page change event when increasing the number of rows causes a page change
      }

      for (let i = pageNumber * rowsPerPage; i < (pageNumber + 1) * rowsPerPage; i++) {
        if (i < table?.length) {
          cb(i);
        }
      }
    },
    [pageNumber, rowsPerPage]
  );

  const isLastPage = (): boolean => {
    const lastPageNumber = Math.floor(table?.length / rowsPerPage);
    return lastPageNumber === pageNumber;
  };

  return {
    syncTable: (displayData: DisplayData, index: number) => {
      table = displayData;
      resourceIdIndex = index;
    },
    add: (resourceId: string, resource: Item) => {
      setSelectedRows(new Map(selectedRows).set(resourceId, resource));
    },
    del: (resourceId: string) => {
      const rows = new Map(selectedRows);
      rows.delete(resourceId);

      setSelectedRows(rows);
    },
    get: (): PageRows => {
      return selectedRows;
    },
    lookup: (resourceID: string): Item => {
      return selectedRows.get(resourceID);
    },
    isChecked: (resourceId: string) => {
      if (selectedRows?.get(resourceId)) {
        return true;
      }
      return false;
    },
    checkAllForPage: () => {
      const rows: PageRows = new Map(selectedRows);

      doPerPage((i: number) => {
        rows.set(String(table[i]?.data?.[resourceIdIndex]), {
          tableIndex: i,
          dataIndex: table[i].dataIndex,
        });
      }, table);

      setSelectedRows(rows);
    },
    checkAllForTable: () => {
      const rows: PageRows = new Map();

      for (let i = 0; i < table.length; i++) {
        rows.set(String(table[i]?.data?.[resourceIdIndex]), {
          tableIndex: i,
          dataIndex: table[i].dataIndex,
        });
      }

      setSelectedRows(rows);
    },
    allCheckedPage: () => {
      if (selectedRows?.size === 0) {
        return false;
      }

      let numChecked = 0;
      doPerPage((i: number) => {
        if (selectedRows.get(String(table[i]?.data?.[resourceIdIndex]))) {
          numChecked++;
        }
      }, table);

      const rows = table?.length;
      if (isLastPage()) {
        // Last page needs different logic because last page might not a full set of rows
        return numChecked === rows % rowsPerPage || numChecked === rows || false;
      }

      return numChecked === rowsPerPage || numChecked === rows || false;
    },
    partiallyCheckedPage: () => {
      if (selectedRows?.size === 0) {
        return false;
      }

      let numChecked = 0;
      doPerPage((i: number) => {
        if (selectedRows.get(String(table[i]?.data?.[resourceIdIndex]))) {
          numChecked++;
        }
      }, table);

      if (isLastPage()) {
        // Last page needs different logic because last page might not a full set of rows
        return numChecked > 0 && numChecked < table?.length % rowsPerPage;
      }

      return numChecked > 0 && numChecked < rowsPerPage;
    },
    clear: () => {
      setSelectedRows(new Map());
    },
    clearPage: () => {
      const rows = new Map(selectedRows);

      doPerPage((i: number) => {
        rows.delete(String(table[i]?.data?.[resourceIdIndex]));
      }, table);

      setSelectedRows(rows);
    },
  };
};
